import { z } from 'zod';
import { moduleKeySchema } from '../../enums/module-key.js';
import { platformRoleKeySchema } from '../../permissions/console.js';
import { moduleAvailabilitySchema } from './console.js';
import { paginatedSchema, paginationQuerySchema } from '../pagination.js';

/** Console · a module an organization asked for (Settings → Modules, «Richiedi attivazione»). */
export const MODULE_REQUEST_STATUSES = ['pending', 'granted', 'declined'] as const;
export const moduleRequestSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  organizationName: z.string(),
  moduleKey: moduleKeySchema,
  note: z.string(),
  status: z.enum(MODULE_REQUEST_STATUSES),
  requestedBy: z.string().nullable(),
  createdAt: z.string().datetime(),
  handledAt: z.string().datetime().nullable(),
});
export type ModuleRequest = z.infer<typeof moduleRequestSchema>;
export const moduleRequestListSchema = z.object({ items: z.array(moduleRequestSchema) });
export const moduleRequestQuerySchema = z.object({
  status: z.enum([...MODULE_REQUEST_STATUSES, 'all']).default('pending'),
});

/** Granting a request makes the module available (included or on trial, never `none`). */
export const moduleRequestGrantSchema = moduleAvailabilitySchema.refine(
  (value) => value.entitlement !== 'none',
  { path: ['entitlement'] },
);

/** Console · the platform team: who holds which platform role. */
export const platformMemberSchema = z.object({
  userId: z.string().uuid(),
  email: z.string(),
  displayName: z.string(),
  role: platformRoleKeySchema,
  createdAt: z.string().datetime(),
});
export type PlatformMember = z.infer<typeof platformMemberSchema>;
export const platformMemberListSchema = z.object({ items: z.array(platformMemberSchema) });
export const platformMemberUpsertSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: platformRoleKeySchema,
});

/** Console · the platform audit trail, newest first. */
export const consoleAuditEntrySchema = z.object({
  id: z.string().uuid(),
  occurredAt: z.string().datetime(),
  organizationId: z.string().uuid().nullable(),
  organizationName: z.string().nullable(),
  actor: z.object({ id: z.string().uuid(), displayName: z.string() }).nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  before: z.unknown(),
  after: z.unknown(),
});
export type ConsoleAuditEntry = z.infer<typeof consoleAuditEntrySchema>;
export const consoleAuditQuerySchema = paginationQuerySchema.extend({
  organizationId: z.string().uuid().optional(),
  action: z.string().trim().max(80).optional(),
});
export const consoleAuditListSchema = paginatedSchema(consoleAuditEntrySchema);
